import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { EventService } from './event.service';
const STORE_KEY = 'lastActionAt';

@UntilDestroy({ checkProperties: true})
@Injectable()
export class AutoLogoutService implements OnDestroy {
  public autoLogoutTriggered: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public showAutoLogoutWarningDlg: BehaviorSubject<number> = new BehaviorSubject(0);
  public autoLogoutRunning: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private autoLogoutAfter: number = 5 * 60 * 1000;       // 5 minutes of idle before we logout
  private warningForAutoLogoutAfter: number = 90 * 1000;  // 90 seconds before auto logout

  private t: Observable<any> = new Observable();
  private onTimeChanged: Subscription;

  private DebugFlag = false;
  private AutoLogoutLoggingFlag = false;

  onDefaultDataChanged$: Subscription;

  get lastAction() {
    const s = window.sessionStorage.getItem(STORE_KEY);
    if (s) {
      return parseInt(s, 10);
    }
    this.lastAction = Date.now();
    return Date.now();
  }
  set lastAction(value) {
    window.sessionStorage.setItem(STORE_KEY, value.toString());
  }

  constructor(
    private ngZone: NgZone,
    private events: EventService,
  ) {
    console.log('Starting AutoLogout Service');
    this.onDefaultDataChanged$ = this.events.onDefaultDataChanged.subscribe(defData => {
      if (defData) {
        let timeOut = defData.customerSettings.AutoLogoutTime;
        if (timeOut < 1) {
          timeOut = 2;
        }
        if (this.DebugFlag) {
          timeOut = 2;
        }

        this.autoLogoutAfter = timeOut * 60 * 1000;
      }
    });

    this.init();
  }

  ngOnDestroy(){
    if (this.onDefaultDataChanged$) {
      this.onDefaultDataChanged$.unsubscribe();
    }
    this.autoLogoutTriggered?.unsubscribe();
    this.showAutoLogoutWarningDlg?.unsubscribe();
    this.autoLogoutRunning?.unsubscribe();
  }

  init() {
    this.ngZone.runOutsideAngular(() => {
      document.body.addEventListener('click', () => this.reset());
    });
    this.showAutoLogoutWarningDlg.next(0);
    this.autoLogoutTriggered.next(false);
    this.autoLogoutRunning.next(false);
  }

  reset() {
    if (this.showAutoLogoutWarningDlg.getValue() === 0) {
      this.lastAction = Date.now();
      if (this.showAutoLogoutWarningDlg.getValue() > 0) {
        this.showAutoLogoutWarningDlg.next(0);
      }
      if (this.autoLogoutTriggered.getValue() === true) {
        this.autoLogoutTriggered.next(false);
      }
    }
  }

  continueSession() {
    this.showAutoLogoutWarningDlg.next(0);
    this.reset();
  }

  start() {
    this.log('Start Auto Logout : ', this.autoLogoutAfter, this.warningForAutoLogoutAfter);
    this.continueSession();
    this.initIntervalTimer();
  }

  initIntervalTimer() {
    // Turn on checking
    this.autoLogoutRunning.next(true);

    // create a timer to perform the checks once a second.
    this.t = timer(1000, 1000);
    this.onTimeChanged = this.t.subscribe(() => {
      this.check();
    });
  }

  stop() {
    if (this.autoLogoutRunning) {
      this.autoLogoutRunning.next(false);
    }
    if (this.onTimeChanged) {
      this.onTimeChanged.unsubscribe();
    }
    this.log('Stop auto Logout');
  }

  check() {
    if (this.autoLogoutRunning.getValue() === false) {
      return;
    }

    const now = Date.now();
    const timeLeft = this.lastAction + this.autoLogoutAfter;
    const diff = timeLeft - now;
    this.log('autoLogout Check ', diff);

    // Fire warning at Warning timeout so we can show dialog before logout.
    if (diff < this.warningForAutoLogoutAfter) {
      if (this.showAutoLogoutWarningDlg.getValue() === 0) {
        this.log('Auto logout Warning time reached - Warn user session will be closed');
        this.showAutoLogoutWarningDlg.next(Math.round(diff / 1000));
      }
    }

    if (diff < 1) {
      if (this.autoLogoutTriggered.getValue() === false) {
        this.log('Auto logout time reached - User not active. Starting Logout');
        this.stop();
        this.autoLogoutTriggered.next(true);
      }
    }
  }

  log(message?: any, ...optionalParams: any[]) {
    if (this.AutoLogoutLoggingFlag) {
      console.log(message, optionalParams);
    }
  }
}
